"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import {
  TABS,
  TALENT_SEARCH_ROOT,
  matchSearchRoute,
  tabHref,
} from "@/components/search/search-tabs";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/stores/sidebar-store";

import { PanelBackButton } from "./panel-back-button";

function SearchTabsPanelRow({
  tab,
  active,
  disabled,
  href,
  onSelect,
}: {
  tab: (typeof TABS)[number];
  active: boolean;
  disabled: boolean;
  href: string | null;
  onSelect: () => void;
}) {
  const content = (
    <>
      <Icon
        name={tab.icon}
        size={16}
        className={cn(
          "size-4 shrink-0 text-contrast",
          active ? "opacity-90" : "opacity-[0.45]",
        )}
      />
      <span className="flex-1 truncate text-sm font-medium leading-5">
        {tab.label}
      </span>
    </>
  );

  if (disabled || !href) {
    return (
      <div
        className="flex h-8 w-full cursor-not-allowed select-none items-center gap-2 rounded-md px-2 opacity-50"
        aria-disabled="true"
      >
        {content}
      </div>
    );
  }

  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      onClick={active ? onSelect : undefined}
      className={cn(
        "flex h-8 w-full items-center gap-2 rounded-md px-2 transition-colors",
        active
          ? "sidebar-active text-heading"
          : "text-contrast/65 hover:bg-contrast/4 hover:text-heading",
      )}
    >
      {content}
    </Link>
  );
}

export function SearchTabsPanel({ onBack }: { onBack: () => void }) {
  const pathname = usePathname();
  const { setOpenMobile } = useSidebar();
  const match = matchSearchRoute(pathname);
  const searchId = match?.searchId ?? null;

  // Only reachable via the "Search" drill row in `main-content.tsx`, which
  // itself only renders on a matching route — so this should be
  // unreachable in practice. Guard anyway rather than crash on a null match.
  if (!match) return null;

  const activeTab = match.activeTab;
  const enabled = !!searchId;

  return (
    <nav aria-label="Search" className="flex flex-col gap-0.5 px-3.5 py-2.5">
      <PanelBackButton onBack={onBack} />

      {TABS.map((tab) => {
        const isSecondary = tab.id !== "search";
        const disabled = isSecondary && !enabled;
        const href = disabled
          ? null
          : enabled
            ? tabHref(searchId!, tab.id)
            : TALENT_SEARCH_ROOT;

        return (
          <SearchTabsPanelRow
            key={tab.id}
            tab={tab}
            active={activeTab === tab.id}
            disabled={disabled}
            href={href}
            onSelect={() => setOpenMobile(false)}
          />
        );
      })}
    </nav>
  );
}
